import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import SecondaryButton from "canvas-core-react/lib/SecondaryButton";

import useApi from "../../../hooks/useApi";
import useModal from "../../../hooks/useModal";
import DeleteAccountModal from "./DeleteAccountModal";
import LoadingAccountModal from "./LoadingAccountModal";
import { moduleAction, sucessInfoModal } from "../../../utils/functions";
import { MOD_CUSTOM, WRITE } from "../../../../permissions/";
import { getRoles } from "../../../../Redux/reducer/session";
import AccountTypeSelect, {
  VALUE_ACCOUNT_TYPE,
} from "../../../shared/AccountTypeSelect";
import BicDestinationBankSelect from "../../../shared/BicDestinationBankSelect";
import {
  getAchBanks,
  getOneProvider,
} from "../../../../Redux/reducer/providers";

// assets
import equis from "../../../../../static/images/X.svg";
import iconEdit from "../../../../../static/images/icon-addnew-18px.svg";
import Modal from "../../../utils/customModal";
import Selector from "canvas-core-react/lib/Selector";

const notApply = "No aplica";

const returnValue = (value, haveDefault = true) => {
  if (value) return value;
  if (haveDefault) return notApply;
  return undefined;
};

export const RenderTopAction = ({
  isEditingAccount,
  hasPerm,
  editClient,
  cancelEdit,
}) => {
  const { t } = useTranslation();

  if (hasPerm && !isEditingAccount)
    return (
      <div className="custom-flex-space">
        <h3 className="flex-element"></h3>
        <div className="flex-element custom-flex-space">
          <p className="add-account mt0" onClick={editClient}>
            {t(`general.labels.edit`)}
          </p>
          <img
            alt=""
            id="edit-button"
            onClick={editClient}
            src={iconEdit}
            className="icon-plus--account mt0"
          />
        </div>
      </div>
    );
  if (hasPerm && isEditingAccount)
    return (
      <div className="custom-flex-space">
        <h3 className="flex-element"></h3>
        <div className="flex-element custom-flex-space">
          <p className="add-account mt0" onClick={cancelEdit}></p>
          <img
            alt=""
            id="cancel-edit-icon"
            onClick={cancelEdit}
            src={equis}
            className="icon-plus--account mt-5"
          />
        </div>
      </div>
    );

  // default
  return null;
};

export const ERROR_MESSAGE_DEFAULT =
  "No se pudo realizar la acción, intente más tarde o ponte en contacto con nosotros.";

const formatErrors = (errors = []) => {
  const newErrors = errors
    .map((item, index) => {
      return item?.description ? (
        <p key={`text-${index}`} id={`error-${index}`}>
          • {item?.description}
        </p>
      ) : null;
    })
    .filter((i) => i);

  if (!newErrors.length) {
    newErrors.push(
      <p key="random-key" id="error-default">
        {ERROR_MESSAGE_DEFAULT}
      </p>
    );
  }

  return newErrors;
};

const CODE_PA = "PA";
const ACCOUNT_INTERNATIONAL = "INT";

const defaultValues = {
  countryCode: "",
  accountType: "",
  accountCurrency: "",
  accountNumber: "",
  bicDestinationBank: "",
  bicIntermediaryBank: "",
  accountIntermediaryBank: "",
};

const ProviderAccountDetail = ({ data, doBack, isEditing = false }) => {
  const { t } = useTranslation();
  const { putApiCAll } = useApi();
  const { show, onToggle } = useModal();
  const [values, setValues] = useState(defaultValues);
  const { show: showError, onToggle: onToggleErrorModal } = useModal();
  const { show: showLoadingModal, onToggle: onToggleLoadingModal } = useModal();

  const [errors, setErrors] = useState([]);
  const [errorForm, setErrorForm] = useState(<span />);
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [loadingEditAccount, setLoadingEditAccount] = useState(false);
  const roles = useSelector((state) => getRoles(state));
  const provider = useSelector((state) => getOneProvider(state));
  const achBanks = useSelector((state) => getAchBanks(state)) || [];

  const onToggleError = () => {
    setErrorForm(<span />);
    onToggleErrorModal();
  };

  const editClient = () => {
    setIsEditingAccount(true);
  };

  const cancelEdit = () => {
    setValues({
      countryCode: provider?.countryCompany?.code,
      accountType: data.accountType,
      accountCurrency:
        provider?.countryCompany?.code === CODE_PA ? "USD" : "CRC",
      accountNumber: data?.accountNumber,
      bicDestinationBank: data?.bicDestinationBank,
      bicIntermediaryBank: data?.bicIntermediaryBank,
      accountIntermediaryBank: data?.accountIntermediaryBank,
    });
    setIsEditingAccount(false);
  };

  const onCloseLoadingModal = () => {
    setErrors([]);
    onToggleLoadingModal();
  };

  const onSubmitForm = async (event) => {
    event?.preventDefault();
    const form = { ...values };

    if (
      form.accountIntermediaryBank === notApply ||
      !form.accountIntermediaryBank
    ) {
      form.accountIntermediaryBank = null;
    }
    if (form.bicDestinationBank === notApply || !form.bicDestinationBank) {
      form.bicDestinationBank = null;
    }
    if (form.bicIntermediaryBank === notApply || !form.bicIntermediaryBank) {
      form.bicIntermediaryBank = null;
    }

    const {
      accountType,
      accountIntermediaryBank,
      bicDestinationBank,
      bicIntermediaryBank,
    } = form;

    if (
      (provider?.countryCompany.code === CODE_PA &&
        accountType === VALUE_ACCOUNT_TYPE.LOCAL &&
        !bicDestinationBank) ||
      (accountType === VALUE_ACCOUNT_TYPE.INT && !bicDestinationBank)
    ) {
      setErrorForm(<p>Por favor ingrese BIC Banco Destino.</p>);
      return onToggleErrorModal();
    }

    if (!form.accountNumber) {
      setErrorForm(<p>Por favor ingrese un número de cuenta.</p>);
      return onToggleErrorModal();
    }

    setLoadingEditAccount(true);
    const payload = {
      iban: data.iban,
      active: data.active,
      id: data.id,
      accountType,
      providerId: data.providerId,
      accountNumber: form.accountNumber,
      accountCurrency: form.accountCurrency,
      accountIntermediaryBank,
      bicDestinationBank,
      bicIntermediaryBank,
    };

    onToggleLoadingModal();

    try {
      const response = await putApiCAll(
        `account/update/${data.providerId}`,
        payload
      );
      const { notifications } = response;
      if (notifications) {
        return setErrors(formatErrors(notifications));
      }
    } catch (error) {
      console.log(error);
      const notifications = error?.response?.data?.notifications || [];
      setErrors(formatErrors(notifications));
    }

    setLoadingEditAccount(false);
  };

  const handleValue = (event) => {
    let restValues = {};
    const { name, value } = event?.target;

    if (name === "accountType") {
      restValues = {
        accountIntermediaryBank: null,
        bicDestinationBank: null,
        bicIntermediaryBank: null,
      };
    }

    setValues((prev) => ({ ...prev, [name]: value, ...restValues }));
  };

  const hasPerm = moduleAction(roles, MOD_CUSTOM, WRITE);

  const verifyEditFields =
    !isEditingAccount ||
    (values.accountType !== ACCOUNT_INTERNATIONAL &&
      provider?.countryCompany?.code !== CODE_PA);

  useEffect(() => {
    setValues({
      countryCode: provider?.countryCompany?.code,
      accountType: data.accountType,
      accountCurrency: data.accountCurrency,
      accountNumber: data?.accountNumber,
      bicDestinationBank: data?.bicDestinationBank,
      bicIntermediaryBank: data?.bicIntermediaryBank,
      accountIntermediaryBank: data?.accountIntermediaryBank,
    });
  }, [data, provider?.countryCompany]);

  useEffect(() => {
    isEditing && setIsEditingAccount(true);
  }, [isEditing]);

  return (
    <>
      <RenderTopAction
        hasPerm={hasPerm}
        editClient={editClient}
        cancelEdit={cancelEdit}
        isEditingAccount={isEditingAccount}
      />
      <form
        id="user-provider"
        method="POST"
        onSubmit={onSubmitForm}
        key={`form-${isEditingAccount}`}
      >
        <div className="row">
          <div className="input col-lg-6 col-user-provider">
            <label className="label label-user-provider">
              {t("general.provider_account_detail.country")}
            </label>
            <input
              readOnly
              disabled
              type="text"
              autoComplete="off"
              id="countryCode"
              name="countryCode"
              onChange={handleValue}
              value={values.countryCode}
              className="input fix-padding input--text input-w-aux__input input--active"
            />
          </div>
          <div className="input col-lg-6 col-user-provider">
            <AccountTypeSelect
              id="account_type"
              name="accountType"
              className="fixed-label-margin"
              disabled={!isEditingAccount}
              value={values.accountType}
              onChange={handleValue}
            />
          </div>

          <div className="input col-lg-6 col-user-provider">
            <Selector
              label={t("general.provider_account_detail.divisa")}
              className="fixed-label-margin"
              id="accountCurrency"
              name="accountCurrency"
              value={values.accountCurrency}
              onChange={handleValue}
              disabled={
                values.accountType === VALUE_ACCOUNT_TYPE.INT ||
                provider?.countryCompany?.code === CODE_PA
              }
            >
              {provider?.countryCompany?.code !== CODE_PA &&
                values.accountType !== VALUE_ACCOUNT_TYPE.INT && (
                  <option key={`state-0`} value={`CRC`}>
                    CRC
                  </option>
                )}
              <option key={`state-1`} value={`USD`}>
                USD
              </option>
            </Selector>
          </div>

          <div className="input col-lg-6 col-user-provider">
            <label className="label label-user-provider">
            {t("general.provider_account_detail.account_number")}
            </label>
            <input
              type="text"
              id="accountNumber"
              name="accountNumber"
              readOnly={!isEditingAccount}
              value={values.accountNumber}
              onChange={handleValue}
              className="input fix-padding input--text input-w-aux__input input--active"
            />
          </div>
          {values.accountType === VALUE_ACCOUNT_TYPE.INT && (
            <>
              <div className="input col-lg-6 col-user-provider">
                <label className="label label-user-provider">
                  {t("general.provider_account_detail.destinatin_bic")}
                </label>
                <input
                  type="text"
                  id="bicDestinationBank"
                  name="bicDestinationBank"
                  key={values.accountType}
                  readOnly={verifyEditFields}
                  value={returnValue(
                    values?.bicDestinationBank,
                    verifyEditFields
                  )}
                  onChange={handleValue}
                  className="input fix-padding input--text input-w-aux__input input--active"
                />
              </div>

              <div className="input col-lg-6 col-user-provider">
                <label className="label label-user-provider">
                  {t("general.provider_account_detail.intermediary_bank")}
                </label>
                <input
                  type="text"
                  id="bicIntermediaryBank"
                  name="bicIntermediaryBank"
                  readOnly={verifyEditFields}
                  className="input fix-padding input--text input-w-aux__input input--active"
                  onChange={handleValue}
                  value={returnValue(
                    values?.bicIntermediaryBank,
                    verifyEditFields
                  )}
                />
              </div>
              <div className="input col-lg-6 col-user-provider">
                <label className="label label-user-provider">
                  {t("general.provider_account_detail.intermediary_account")}
                </label>
                <input
                  type="text"
                  readOnly={verifyEditFields}
                  id="accountIntermediaryBank"
                  name="accountIntermediaryBank"
                  className="input fix-padding input--text input-w-aux__input input--active"
                  onChange={handleValue}
                  value={returnValue(
                    values?.accountIntermediaryBank,
                    verifyEditFields
                  )}
                />
              </div>
            </>
          )}

          {values.accountType === VALUE_ACCOUNT_TYPE.LOCAL &&
            provider?.countryCompany?.code === CODE_PA && (
              <div className="input col-lg-6 col-user-provider">
                <BicDestinationBankSelect
                  items={achBanks}
                  id="bicDestinationBank"
                  name="bicDestinationBank"
                  disabled={!isEditingAccount}
                  onChange={handleValue}
                  value={values?.bicDestinationBank || undefined}
                />
              </div>
            )}
        </div>
      </form>

      <div className="form-actions clearfix">
        <SecondaryButton onClick={isEditingAccount ? cancelEdit : doBack}>
          {isEditingAccount
            ? t("general.special_codition.CANCEL")
            : t("general.special_codition.BACK")}
        </SecondaryButton>

        {hasPerm && !isEditingAccount ? (
          <SecondaryButton id="delete-account-detail" onClick={onToggle}>
            {t("general.provider_account_detail.delete")}
          </SecondaryButton>
        ) : null}
        {isEditingAccount ? (
          <SecondaryButton
            type="submit"
            form="user-provider"
            id="edit-account-detail"
          >
            {t("general.provider_account_detail.save")}
          </SecondaryButton>
        ) : null}
      </div>

      <DeleteAccountModal
        show={show}
        onCancel={onToggle}
        accountId={data?.id}
        providerId={data?.providerId}
        onOk={() => {
          onToggle();
          doBack && doBack();
        }}
      />

      <LoadingAccountModal
        errors={errors}
        show={showLoadingModal}
        onOkFail={onCloseLoadingModal}
        onOk={doBack}
        loading={loadingEditAccount}
      />

      <Modal show={showError} handleClose={onToggleError}>
        {sucessInfoModal("Verificar información", errorForm, {
          formBtn: { text: "Cerrar", action: onToggleError },
        })}
      </Modal>
    </>
  );
};

export default ProviderAccountDetail;
